import React, { useState, useEffect } from "react";
import {
  useTheme,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import baseURL from "../config";
import voucher from "../Assets/S&FVoucher.png";
import { ToastContainer, toast } from "react-toastify";
import CryptoJS from "crypto-js";
import Cookies from "js-cookie";
import { tokens } from "../theme";

const VoucherPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [voucherCode, setVoucherCode] = useState(""); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState("");
  const [posId, setPosId] = useState("");
  const [amount, setAmount] = useState("");
  const [activeVouchers, setActiveVouchers] = useState([]);
  const [poses, setPoses] = useState([]);
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [voucherTypeId, setVoucherType] = useState("");
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");

  const getDecryptedToken = () => {
    const encryptedToken = Cookies.get("token");
    if (!encryptedToken) return null;

    const secretKey = '19092002'; 
    const bytes = CryptoJS.AES.decrypt(encryptedToken, secretKey);
    const decryptedToken = bytes.toString(CryptoJS.enc.Utf8);

    return decryptedToken || null;
  };

  const token = getDecryptedToken();

  const handleCreateVoucher = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${baseURL}/api/Voucher/CreateVoucher`, {
        userId,
        posId,
        amount,
      });

      if (response.status !== 200) {
        throw new Error("Failed to generate voucher code");
      }

      setVoucherCode(response.data.voucherCode);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchActiveVouchers = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/Voucher/GetAllVouchers`);

        if (response.data.success === true) {
          setActiveVouchers(response.data.vouchers);
        }
      } catch (err) {
        console.error("Failed to fetch active vouchers:", err.message);
      }
    };

    fetchActiveVouchers();
  }, []);

  useEffect(() => {
    const fetchPos = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/PointOfSale/GetPos`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data && Array.isArray(response.data)) {
          setPoses(response.data);
        } else {
          setPoses([]); 
          toast.error("Invalid POS data received");
        }
      } catch (err) {
        toast.error("Failed to fetch POS");
        setPoses([]);
      }
    };
    fetchPos();
  }, [token]);

  useEffect(() => {
    const fetchVoucherTypes = async () => {
      try{
        const response = await axios.get(`${baseURL}/api/Voucher/GetVoucherTypes`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if(response.data.success === true){
          setVoucherTypes(response.data.voucherTypes);
        }else{
          setVoucherTypes([]);
          toast.error("Invalid voucher types data received");
        }
      }catch(err){
        toast.error("Failed to fetch voucher types");
    }
  };
  fetchVoucherTypes();
  },[token]);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/Brands/GetBrands`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data.success === true) {
          setBrands(response.data.brands);
        } else {
          setBrands([]);
          toast.error("Invalid brands data received");
        }
      } catch (err) {
        toast.error("Failed to fetch brands");
        setBrands([]);
      }
    };
    fetchBrands();
  }, [token]);

  // Filter POS based on selected brand
  const filteredPoses = selectedBrand
    ? poses.filter(pos => pos.brandId === selectedBrand)
    : poses;

  // Columns for the DataGrid
  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "voucherCode", headerName: "Voucher Code", width: 200 },
    { field: "posId", headerName: "POS ID", width: 120 },
    { field: "purchasedBy", headerName: "Purchased By", width: 140 },
    {
      field: "dateOfPurchase",
      headerName: "Date of Purchase",
      width: 180,
      // Format the date
    },
    { field: "assignedTo", headerName: "Assigned To", width: 140 },
    { field: "voucherStatus", headerName: "Status", width: 120 },
    {
      field: "dateOfUsage",
      headerName: "Date of Usage",
      width: 180,
    },
  ];

  return (
    <div style={{ textAlign: "center", padding: "40px" }}>
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Create a Voucher
      </Typography>

      {/* Input Fields */}
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} sm={4}>
          <TextField
            label="User ID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            fullWidth
            size="small"
            margin="normal"
            variant="outlined"
            color={colors.primary[400]}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel color={colors.primary[400]}>Brand</InputLabel>
            <Select
              value={selectedBrand || ""}
              onChange={(e) => {
                setSelectedBrand(e.target.value);
                setPosId(""); // Reset POS ID when brand changes
              }}
              variant="outlined"
              color={colors.primary[400]}
            >
              {brands.length > 0 ? (
                brands.map((brand) => (
                  <MenuItem key={brand.brandId} value={brand.brandId}>
                    {brand.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No Brands available</MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel color={colors.primary[400]}>POS ID</InputLabel>
            <Select
              value={posId || ""}
              onChange={(e) => setPosId(e.target.value)}
              variant="outlined"
              color={colors.primary[400]}
            >
              {filteredPoses.length > 0 ? (
                filteredPoses.map((pos) => (
                  <MenuItem key={pos.posid} value={pos.posid}>
                    {pos.name || "Unnamed POS"}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No POS available</MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel color={colors.primary[400]}>Voucher Type</InputLabel>
            <Select
              value={voucherTypeId || ""}
              onChange={(e) => {
                const selectedVoucher = voucherTypes.find(voucher => voucher.voucherTypeId === e.target.value);
                setVoucherType(e.target.value);
                setAmount(selectedVoucher ? selectedVoucher.amount : "");
              }}
              variant="outlined"
              color={colors.primary[400]}
            >
              {voucherTypes.length > 0 ? (
                voucherTypes.map((voucher) => (
                  <MenuItem key={voucher.voucherTypeId} value={voucher.voucherTypeId}>
                    {voucher.amount || "No Voucher Types"}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No Voucher available</MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Voucher Image and Code */}
      <div style={{ position: "relative", display: "inline-block", margin: "20px 0" }}>
        <img
          src={voucher}
          alt="Voucher"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "cover",
            borderRadius: "10px",
          }}
        />
        {voucherCode && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -77%)",
              backgroundColor: "transparent",
              padding: "5px 15px",
              borderRadius: "5px",
              fontSize: "18px",
              fontWeight: "bold",
              color: "#333",
              textAlign: "center",
            }}
          >
            {voucherCode}
          </div>
        )}
      </div>

      {/* Create Voucher Button */}
      <div style={{ marginTop: "20px" }}>
        <Button
          variant="contained"
          
          onClick={handleCreateVoucher}
          style={{ padding: "10px 20px", fontSize: "16px", backgroundColor: colors.primary[700] }}
          disabled={loading || !userId || !posId || !amount}
        >
          {loading ? <CircularProgress size={24} style={{ color: "white " }} /> : "Create Voucher"}
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <Typography variant="body1" color="error" style={{ marginTop: "20px" }}>
          {error}
        </Typography>
      )}

      {/* Active Vouchers Table */}
      <div style={{ marginTop: "40px", textAlign: "left" }}>
        <Typography variant="h5" gutterBottom>
          Active Vouchers
        </Typography>
        <div style={{ height: 400, width: "100%" }}>
          <DataGrid
            rows={activeVouchers}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
            getRowId={(row) => row.id} // Ensure unique IDs for rows
          />
        </div>
      </div>
    </div>
  );
};

export default VoucherPage;